import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState, } from "react";
import { Box, Paper, Autocomplete, TextField, FormControl, Grid, Button, Switch, FormGroup, FormControlLabel, Typography, InputLabel, Select, MenuItem, } from "@mui/material";
import { DataGrid, } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import CustomModal from "../modal/CustomModal";
import { toast } from "react-toastify";
import LeadsApi from "../../../infrastructure/api/pages/lead-api";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import { statusBgColor, statusMapping, statusTextColor, } from "../../../application/common/utils/column-helper";
import { Star } from "@mui/icons-material";
import { getStatusOptionsForDropdown } from "../../../application/common/utils/lead-status-helper";
const SearchIconWrapper = styled("div")(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: "100%",
    position: "absolute",
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
}));
const AssignMultipleLeadsModal = ({ open, onClose, refresh, setRefreshModalData, contactOptions, }) => {
    const [selection, setSelection] = useState({
        type: "include",
        ids: new Set(),
    });
    const [error, setError] = useState(undefined);
    const [status, setStatus] = useState();
    const [disable, setDisable] = useState(true);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 15,
    });
    const [totalLeads, setTotalLeads] = useState(0);
    const personSelectRef = useRef(null);
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState(search);
    const [isSelectiveRoles, setIsSelectiveRoles] = useState(true);
    const [isUnAssignedLead, setIsUnAssignedLead] = useState(false);
    const handleSearch = (e) => {
        if (e.key === "Enter") {
            setSearch(searchInput);
        }
    };
    const [paginnationModelProp, setPaginnationModelProp] = useState(null);
    const getAllLeads = () => {
        setLoading(true);
        let pageChanged = paginnationModelProp !== paginationModel;
        LeadsApi.getAllLeads(paginationModel.page, paginationModel.pageSize, search, status, isSelectiveRoles, pageChanged, isUnAssignedLead)
            .then((response) => {
            if (response.status === 200 && response.data != null) {
                setLeads(response.data);
                let data = response.data;
                if (data.length > 0) {
                    setTotalLeads(data[0].totalRows);
                }
            }
        })
            .finally(() => {
            setTimeout(() => setLoading(false), 400);
        });
        setPaginnationModelProp(paginationModel);
    };
    useEffect(() => {
        getAllLeads();
    }, [paginationModel, search, refresh, status, isSelectiveRoles]);
    const [saving, setSaving] = useState(false);
    const { t: translate } = useTranslation();
    const [selectedPerson, setSelectedPerson] = useState(null);
    const handleAutocompleteChange = (event, newValue) => {
        setSelectedPerson(newValue);
        if (!newValue) {
            setError("Verplicht");
        }
        else {
            setError(undefined);
        }
    };
    const handleSaveButtonClick = () => {
        var _a;
        if (selectedPerson) {
            const selectedRowsData = Array.from(selection.ids).map((id) => id);
            if (selectedRowsData) {
                var assignMultipleLead = {
                    ResponsiblePerson: selectedPerson === null || selectedPerson === void 0 ? void 0 : selectedPerson.label,
                    ResponsiblePersonId: selectedPerson === null || selectedPerson === void 0 ? void 0 : selectedPerson.id,
                    LeadIds: selectedRowsData.map((x) => x),
                };
                if (assignMultipleLead) {
                    setSaving(true);
                    setDisable(true);
                    LeadsApi.assignMultipleLead(assignMultipleLead)
                        .then((response) => {
                        if (response.status === 200 && response.data != null) {
                            toast.success(translate("Responsible Person Assigned Successfully"));
                            onCloseModal();
                        }
                        else {
                            setDisable(false);
                        }
                    })
                        .finally(() => {
                        setSaving(false);
                        setRefreshModalData((prev) => !prev);
                    });
                }
            }
        }
        else {
            setError("Verplicht");
            (_a = personSelectRef === null || personSelectRef === void 0 ? void 0 : personSelectRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    const onCloseModal = () => {
        setSearchInput("");
        setSearch("");
        setSelection({ type: "include", ids: new Set() });
        setSelectedPerson(null);
        setPaginationModel({ page: 0, pageSize: 15 });
        setError(undefined);
        setDisable(true);
        onClose();
    };
    const columnsForDataGrid = [
        {
            field: "leadNumber",
            headerName: "Nummer",
            sortable: true,
            flex: 1,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: true,
            hideSortIcons: false,
            width: 10,
            renderCell: (params) => (_jsxs(Box, { sx: {
                    display: "flex",
                    alignItems: "center"
                }, children: [params.row.isNewLeadImport && ( // Check if the row is new
                    (_jsx(Star, { sx: { color: "gold", marginRight: "5px" } })) // Add the star icon with styling
                    ), params.row.leadNumber] })),
        },
        {
            field: "contactName",
            headerName: translate("ContactName"),
            sortable: true,
            flex: 1,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: true,
            hideSortIcons: false,
            minWidth: 100,
        },
        {
            field: "responsiblePersonName",
            headerName: translate("ResponsiblePerson"),
            sortable: true,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: true,
            hideSortIcons: false,
            flex: 1,
            minWidth: 100,
        },
        {
            field: "totalObjects",
            headerName: translate("TotalObjects"),
            sortable: true,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: true,
            hideSortIcons: false,
            flex: 1,
            minWidth: 100,
        },
        {
            field: "status",
            headerName: translate("Status"),
            sortable: true,
            align: "center",
            headerAlign: "center",
            hideable: false,
            filterable: true,
            hideSortIcons: true,
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (_jsx(Grid, { container: true, sx: {
                    alignContent: "center",
                    justifyContent: "center"
                }, children: _jsx(Grid, { children: _jsx(Box, { sx: {
                            bgcolor: statusBgColor[params.row.status],
                            padding: 1,
                            mt: 1,
                            borderRadius: 2
                        }, children: _jsx(Typography, { variant: "h5", sx: { color: statusTextColor[params.row.status] }, children: translate(`${statusMapping[params.row.status]}`) }) }) }) })),
        },
    ];
    const onRowsSelectionHandler = (ids) => {
        setSelection(ids);
        if (ids.ids.size > 0) {
            setDisable(false);
        }
        else {
            setDisable(true);
        }
    };
    const handleChange = () => {
        setPaginationModel({ page: 0, pageSize: 15 });
        setIsSelectiveRoles((prev) => !prev);
    };
    const handleUnAssignedChange = () => {
        setIsUnAssignedLead((prev) => !prev);
        setPaginationModel({ page: 0, pageSize: 15 });
    };
    return (_jsxs(CustomModal, { open: open, onClose: onCloseModal, size: "lg", saving: saving, handleSaveButtonClick: handleSaveButtonClick, heading: translate("Verantwoordelijke toewijzen"), saveButtonText: translate("Toewijzen"), disabled: disable, children: [_jsx(Box, { sx: {
                    marginTop: "1%",
                    padding: "16px 32px",
                    borderRadius: "24px",
                    width: "95%",
                }, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { size: {
                                xs: 12,
                                md: 4
                            }, children: _jsx(Autocomplete, { disablePortal: true, id: "combo-box-demo", fullWidth: true, options: contactOptions, noOptionsText: translate("noOptions"), onChange: handleAutocompleteChange, renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: "Selecteer verantwoordelijke", error: !!error, helperText: error, inputRef: personSelectRef }))) }) }), _jsx(Grid, { size: {
                                xs: 12,
                                md: 1.5
                            }, children: _jsx(FormGroup, { children: _jsx(FormControlLabel, { control: _jsx(Switch, { checked: !isSelectiveRoles, onChange: () => handleChange() }), label: "Alle rollen", labelPlacement: "bottom" }) }) }), _jsx(Grid, { size: {
                                xs: 12,
                                md: 1.5
                            }, children: _jsx(FormGroup, { children: _jsx(FormControlLabel, { control: _jsx(Switch, { checked: isUnAssignedLead, onChange: () => handleUnAssignedChange() }), label: "Niet-toegewezen leads", labelPlacement: "bottom" }) }) }), _jsx(Grid, { size: {
                                xs: 12,
                                md: 2
                            }, children: _jsxs(FormControl, { fullWidth: true, variant: "outlined", size: "medium", children: [_jsx(InputLabel, { children: "Status" }), _jsxs(Select, { labelId: "simple-select-label", id: "simple-select", label: "Status", value: status !== 0 ? status : undefined, onChange: (e) => {
                                            if (e.target.value === 0) {
                                                setStatus(undefined);
                                            }
                                            else {
                                                setStatus(e.target.value);
                                            }
                                        }, children: [_jsx(MenuItem, { value: 0, children: translate("All") }), getStatusOptionsForDropdown(status).map((statusOption) => (_jsxs(MenuItem, { value: statusOption.value, sx: statusOption.deprecated ? {
                                                    fontStyle: 'italic',
                                                    color: 'text.secondary',
                                                    '&:hover': {
                                                        backgroundColor: 'action.hover'
                                                    }
                                                } : {}, children: [translate(statusOption.translationKey), statusOption.deprecated && ` (${translate("Deprecated")})`] }, statusOption.value)))] })] }) }), _jsx(Grid, { size: {
                                xs: 12,
                                md: 2
                            }, children: _jsx(FormControl, { variant: "outlined", size: "medium", children: _jsx(TextField, { id: "search", label: "Lead zoeken", value: searchInput, onChange: (e) => setSearchInput(e.target.value), onKeyDown: (e) => handleSearch(e), variant: "outlined", placeholder: "ID # , Naam , etc...", size: "medium", children: _jsx(SearchIconWrapper, { children: _jsx(SearchIcon, {}) }) }) }) }), _jsx(Grid, { size: {
                                xs: 12,
                                md: 1
                            }, children: _jsx(Button, { variant: "contained", size: "medium", onClick: () => setSearch(searchInput), children: "Zoekopdracht" }) })] }) }), _jsx(Box, { sx: {
                    marginTop: "1%",
                    padding: "16px 32px",
                    borderRadius: "24px",
                }, children: _jsx(Paper, { sx: {
                        width: "100%",
                        overflow: "hidden",
                        boxShadow: "none",
                    }, children: _jsx(DataGrid, Object.assign({ loading: loading, autoHeight: true, rows: leads !== null && leads !== void 0 ? leads : [], onRowSelectionModelChange: (ids) => onRowsSelectionHandler(ids) }, leads, { columns: columnsForDataGrid, 
                        // initialState={{
                        //   pagination: {
                        //     paginationModel: {
                        //       pageSize: 15,
                        //     },
                        //   },
                        // }}
                        rowCount: totalLeads, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, paginationMode: "server", 
                        // pageSizeOptions={[15]}
                        checkboxSelection: true, localeText: { noRowsLabel: translate("Geen rijen"), noResultsOverlayLabel: translate("Geen resultaten") }, disableRowSelectionOnClick: true, keepNonExistentRowsSelected: true, hideFooterPagination: !(leads === null || leads === void 0 ? void 0 : leads.length), rowHeight: 80, sx: {
                            border: "none",
                            fontSize: "14px",
                            "& .has-contract-row": {
                                backgroundColor: "#ffff",
                            },
                            "& .MuiDataGrid-columnHeaderTitle ": {
                                fontSize: "20px",
                                fontWeight: 400,
                            },
                            "& .MuiDataGrid-cell": {
                                display: "flex",
                                alignItems: "center",
                            },
                        }, sortingOrder: ["asc", "desc"] }), "leads-grid") }) })] }));
};
export default AssignMultipleLeadsModal;
